// Completion for a HollowScript editor. The native analysis handle answers the
// language's own part: the names in scope at the cursor (declaration-order
// visibility, with the checker's inferred types as `detail`), then the
// prelude, then every keyword a program can write today. Studio merges its
// own data over that, in the priority order the original brief set (the
// capability surface first, scope second):
//
//   1. Capabilities. The native list holds only the names a `uses` line has
//      already declared; Studio also offers every capability the project
//      *grants* but the script has not yet `uses`d, with its real signatures
//      from `capabilityCatalog.ts` (the native declarations), so a creator can
//      discover one and then add the `uses` line. After `entities.` the
//      capability's own members are offered.
//   2. The standard library: Studio's prose (`stdlib.ts`) attached to the
//      matching prelude names, and the String and List methods after a `.`,
//      narrowed to the receiver's real type when the checker can say what it
//      is (`Analysis.hover` on the receiver).
//
// Known gaps, both the native build's own and documented in its README: it
// offers nothing after a `.` (a field or method needs the receiver's type and
// the members of that type), and no variant names inside a `match` arm. A
// record field after a `.` therefore completes nothing; the stdlib and
// capability members above are the only member completions there are. One
// more, not in its README: at the very end of a file, with no text after the
// `.`, it does not notice the dot and answers the plain scope list, which is
// why a member access is recognised here from the text before the cursor.
//
// Returns plain data - see `tokenize.ts`'s own doc comment for why no
// `monaco-editor` import belongs in this module.

import type { Analysis, NativeCompletion, NativeCompletionKind, NativeModules } from '../native/hollowscript.js';
import type { StudioCapabilityName } from '../capabilityNames.js';
import type { MonacoTooling } from '../nativeTooling.js';
import { buildAnalysisInput, withAnalysis } from './analysis.js';
import {
  capabilityMembers,
  capabilityNamespaceDetail,
  grantedCapabilityNames,
  loadCapabilityCatalog,
  type CapabilityCatalog,
} from './capabilityCatalog.js';
import {
  STDLIB_ALL_FREE_FUNCTIONS,
  STDLIB_RECEIVER_METHODS,
  stdlibMemberName,
  stdlibReceiverForType,
  type StdlibMember,
} from './stdlib.js';

export type HollowScriptCompletionKind =
  | 'keyword'
  | 'capability'
  | 'namespace'
  | 'method'
  | 'function'
  | 'variable'
  | 'constant'
  | 'parameter'
  | 'type'
  | 'enum';

export interface HollowScriptCompletionItem {
  readonly label: string;
  readonly kind: HollowScriptCompletionKind;
  readonly detail: string | undefined;
  readonly documentation: string | undefined;
  readonly insertText: string;
  /** Orders the list: names in scope first, then capabilities the script has
   * not yet declared, then the prelude, then keywords. */
  readonly sortText: string;
}

export interface HollowScriptCompletionParams {
  readonly source: string;
  /** A UTF-16 offset into `source` (Monaco's `model.getOffsetAt`). */
  readonly offset: number;
  /** The current line's text up to (not including) the cursor - used only to
   * tell a `name.` member-access context (and a comment or string, where
   * nothing is offered) from an ordinary one. Monaco's own model already has
   * this cheaply available (`model.getLineContent`), so this module takes a
   * plain string rather than reaching for a model/position type itself. */
  readonly lineTextBeforeCursor: string;
  readonly grantedCapabilities: ReadonlySet<StudioCapabilityName> | undefined;
  readonly entryPath?: string;
  /** The other scripts an `import` resolves against, by canonical path:
   * without them an imported name is simply not in scope. */
  readonly modules?: NativeModules;
  /** The editor model this belongs to, so it shares that model's one live
   * analysis handle - see `analysis.ts`. */
  readonly key?: string;
}

const GROUP_SCOPE = '0';
const GROUP_CAPABILITY = '1';
const GROUP_PRELUDE = '2';
const GROUP_KEYWORD = '3';
const GROUP_MEMBER = '0';

const KIND_FOR_NATIVE: Readonly<Record<NativeCompletionKind, HollowScriptCompletionKind>> = {
  variable: 'variable',
  constant: 'constant',
  function: 'function',
  parameter: 'parameter',
  type: 'type',
  enum: 'enum',
  capability: 'capability',
  namespace: 'namespace',
  keyword: 'keyword',
};

/** `.`-then-optional-word at the end of the text before the cursor. */
const MEMBER_CONTEXT_RE = /\.([A-Za-z_][A-Za-z0-9_]*)?$/;

function sortText(group: string, label: string): string {
  return `${group}${label.toLowerCase()}`;
}

function freeFunctionDocs(): ReadonlyMap<string, StdlibMember> {
  const docs = new Map<string, StdlibMember>();
  for (const member of STDLIB_ALL_FREE_FUNCTIONS) docs.set(stdlibMemberName(member), member);
  return docs;
}

const FREE_FUNCTION_DOCS = freeFunctionDocs();

/** Whether the cursor sits inside a comment or a string, where nothing is
 * offered. Native completion answers `[]` there; this is for the one place
 * Studio adds names itself, after a `.`. */
function insideCommentOrString(lineTextBeforeCursor: string, highlight: (text: string) => readonly { start: number; end: number; class: string }[]): boolean {
  if (lineTextBeforeCursor.length === 0) return false;
  const last = lineTextBeforeCursor.length - 1;
  return highlight(lineTextBeforeCursor).some((range) => range.start <= last && last < range.end && (range.class === 'comment' || range.class === 'string'));
}

function fromNative(item: NativeCompletion, catalog: CapabilityCatalog): HollowScriptCompletionItem {
  const kind = KIND_FOR_NATIVE[item.kind];
  if (kind === 'capability') {
    const detail = capabilityNamespaceDetail(catalog, item.label);
    return {
      label: item.label,
      kind,
      detail: detail ?? item.detail,
      documentation: detail === undefined ? undefined : `Capability declared by \`uses ${item.label}\`.`,
      insertText: item.label,
      sortText: sortText(GROUP_SCOPE, item.label),
    };
  }
  if (item.detail === 'prelude') {
    const doc = FREE_FUNCTION_DOCS.get(item.label);
    return {
      label: item.label,
      kind,
      detail: doc ? doc.signature : item.detail,
      documentation: doc ? `${doc.receiver}: ${doc.doc}` : undefined,
      insertText: item.label,
      sortText: sortText(GROUP_PRELUDE, item.label),
    };
  }
  if (kind === 'keyword') {
    return { label: item.label, kind, detail: undefined, documentation: undefined, insertText: item.label, sortText: sortText(GROUP_KEYWORD, item.label) };
  }
  return { label: item.label, kind, detail: item.detail === '' ? undefined : item.detail, documentation: undefined, insertText: item.label, sortText: sortText(GROUP_SCOPE, item.label) };
}

function methodItem(member: StdlibMember): HollowScriptCompletionItem {
  const name = stdlibMemberName(member);
  return { label: name, kind: 'method', detail: member.signature, documentation: `${member.receiver}: ${member.doc}`, insertText: name, sortText: sortText(GROUP_MEMBER, name) };
}

/** The stdlib methods to offer after a `.`, de-duplicated by name (`sort` has
 * two overloads, `length` is on both String and List) and narrowed to the
 * receiver's group when its type is known. A receiver whose type is known but
 * is neither a String nor a list gets none: this file documents no other
 * type's methods, and a wrong list is worse than an empty one. */
function stdlibMethodItems(receiverType: string | undefined): readonly HollowScriptCompletionItem[] {
  const group = receiverType === undefined ? undefined : stdlibReceiverForType(receiverType);
  if (receiverType !== undefined && group === undefined) return [];
  const seen = new Set<string>();
  const items: HollowScriptCompletionItem[] = [];
  for (const member of STDLIB_RECEIVER_METHODS) {
    if (group !== undefined && member.receiver !== group) continue;
    const name = stdlibMemberName(member);
    if (seen.has(name)) continue;
    seen.add(name);
    items.push(methodItem(member));
  }
  return items;
}

/** The checker's type for the expression that ends just before the `.` at
 * `dotOffset`, or `undefined` when it cannot say. Asked of a *repaired* copy
 * of the source with the half-typed `.word` taken out: with it in, the text
 * does not parse and the checker types nothing around it, which is exactly
 * the state completion is asked in. The copy is analysed transiently and
 * disposed before returning, so the model's own single live handle is
 * untouched. */
function receiverTypeBeforeDot(tooling: MonacoTooling, params: HollowScriptCompletionParams, dotOffset: number): string | undefined {
  if (dotOffset <= 0) return undefined;
  const repaired = params.source.slice(0, dotOffset) + params.source.slice(params.offset).replace(/^[A-Za-z0-9_]*/, '');
  const input = buildAnalysisInput({ source: repaired, entryPath: params.entryPath, grantedCapabilities: params.grantedCapabilities, modules: params.modules });
  let analysis: Analysis;
  try {
    analysis = tooling.analyse(input.source, input.entryPath, input.declarations, input.modules);
  } catch {
    return undefined;
  }
  try {
    return analysis.hover(dotOffset - 1)?.type;
  } finally {
    analysis.dispose();
  }
}

/** The full completion list at `params.offset`. Never throws on any text: the
 * native build analyses a program with errors all the same, so an in-progress,
 * syntactically broken edit (the common case while a creator is mid-keystroke)
 * still yields whatever scope the checker could work out around it. */
export async function computeHollowScriptCompletions(params: HollowScriptCompletionParams): Promise<readonly HollowScriptCompletionItem[]> {
  const catalog = await loadCapabilityCatalog();
  const result = await withAnalysis(
    { source: params.source, entryPath: params.entryPath, grantedCapabilities: params.grantedCapabilities, modules: params.modules, key: params.key },
    (analysis, tooling): readonly HollowScriptCompletionItem[] => {
      // A member access is detected here, not from an empty native answer: the
      // native build answers nothing after a `.` only when text follows it,
      // and at the very end of a file (a REPL line, the last line of a script
      // with no trailing newline) it offers the plain scope list instead.
      const member = MEMBER_CONTEXT_RE.exec(params.lineTextBeforeCursor);
      if (member) {
        const dotOffset = params.offset - member[0].length;
        const before = params.source.slice(0, dotOffset);
        // `a..b` is the range operator and `3.` the start of a float, not
        // member accesses; a `.` in a comment or string is just text.
        const notMember =
          params.source[dotOffset - 1] === '.' ||
          (member[1] === undefined && /[0-9]$/.test(before)) ||
          insideCommentOrString(params.lineTextBeforeCursor, (text) => tooling.highlight(text));
        if (!notMember) {
          const receiverName = /([A-Za-z_][A-Za-z0-9_]*)$/.exec(before)?.[1];
          if (receiverName !== undefined && (grantedCapabilityNames(params.grantedCapabilities) as readonly string[]).includes(receiverName)) {
            const members = capabilityMembers(catalog, receiverName);
            if (members.length > 0) {
              return members.map((capabilityMember) => ({
                label: capabilityMember.name,
                kind: 'method',
                detail: capabilityMember.signature,
                documentation: `Capability method - requires \`uses ${receiverName}\`.`,
                insertText: capabilityMember.name,
                sortText: sortText(GROUP_MEMBER, capabilityMember.name),
              }));
            }
          }
          return stdlibMethodItems(receiverTypeBeforeDot(tooling, params, dotOffset));
        }
      }

      const items: HollowScriptCompletionItem[] = analysis.completions(params.offset).map((item) => fromNative(item, catalog));
      // Nothing was in scope to offer (a comment, a string): nothing to add.
      if (items.length === 0) return items;

      // A granted capability the script has not declared yet is still worth
      // offering: accepting it is how a creator discovers it, then adds `uses`.
      const declared = new Set(items.filter((item) => item.kind === 'capability').map((item) => item.label));
      for (const name of grantedCapabilityNames(params.grantedCapabilities)) {
        if (declared.has(name)) continue;
        items.push({
          label: name,
          kind: 'capability',
          detail: capabilityNamespaceDetail(catalog, name),
          documentation: `Granted capability - add \`uses ${name}\` to use it.`,
          insertText: name,
          sortText: sortText(GROUP_CAPABILITY, name),
        });
      }
      return items;
    },
  );
  return result ?? [];
}
